import { forwardRef, useState, InputHTMLAttributes, CSSProperties } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * PasswordInput — input with a local show/hide eye toggle.
 *
 * Wraps a standard <input> in a relatively-positioned <div> and overlays an
 * accessible button that flips the input's type between "password" and
 * "text". Toggle state is local to each instance — rendering multiple
 * PasswordInput components on the same page is safe.
 *
 * The component forwards its ref to the underlying <input>, so callers that
 * need imperative focus (e.g. Step1Infrastructure's tokenInputRef) still work.
 *
 * Padding on the right of the input is always set to 44px via inline style so
 * the toggle button does not overlap the entered text. Any `paddingRight` a
 * caller passes in `style` is ignored — the button position is fixed by the
 * component contract.
 */
export type PasswordInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> & {
  /** Extra style applied to the wrapping div (positioning, width, etc.). */
  wrapperStyle?: CSSProperties;
  /** className applied to the wrapping div. */
  wrapperClassName?: string;
  /**
   * aria-label for the visibility toggle button. Defaults to
   * "Show password" / "Hide password" depending on state.
   */
  toggleLabelShow?: string;
  toggleLabelHide?: string;
};

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    {
      style,
      wrapperStyle,
      wrapperClassName,
      toggleLabelShow = 'Show password',
      toggleLabelHide = 'Hide password',
      ...rest
    },
    ref,
  ) {
    const [visible, setVisible] = useState(false);

    // Fixed 44px right padding so the input text can't run under the button.
    // Callers pass their own style but we win on paddingRight.
    const inputStyle: CSSProperties = { ...style, paddingRight: 44 };

    const wrapper: CSSProperties = {
      position: 'relative',
      display: 'block',
      ...wrapperStyle,
    };

    return (
      <div style={wrapper} className={wrapperClassName}>
        <input
          {...rest}
          ref={ref}
          type={visible ? 'text' : 'password'}
          style={inputStyle}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? toggleLabelHide : toggleLabelShow}
          aria-pressed={visible}
          disabled={rest.disabled}
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: rest.disabled ? 'default' : 'pointer',
            padding: 6,
            display: 'flex',
            alignItems: 'center',
            color: 'var(--text-secondary)',
            opacity: rest.disabled ? 0.4 : 1,
          }}
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    );
  },
);

export default PasswordInput;
